/**
 * ML145b - cache local de LEITURA do catalogo e dos precos.
 *
 * - So leituras `catalogo.list|get|operate-list|operate-get`; nada de cliente.
 * - Chaveado por empresa + usuario; sem escopo (anonimo) nada e gravado nem lido.
 * - Com conexao a rede sempre ganha: cada leitura bem-sucedida substitui a copia
 *   (a versao e um resumo do conteudo e muda quando preco ou item muda). A copia
 *   so serve quando a rede falha, nunca quando o servidor responde.
 * - Qualquer escrita em `catalogo.*` invalida a empresa; sair da conta limpa tudo.
 * - Vendas continuam exigindo conexao (SEM_INTERNET_TRANSPARENCIA).
 */
export interface CacheStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  readonly length: number;
  key(index: number): string | null;
}

export interface CatalogCacheScope {
  userId: string;
  companyId: string;
}

const PREFIX = 'rimati:catalog-cache:v1:';
const MAX_ENTRY_CHARS = 1_500_000;
const READ_TOOLS = new Set(['catalogo.list', 'catalogo.get', 'catalogo.operate-list', 'catalogo.operate-get']);

/**
 * ML156a - LISTA DE CAMPOS PERMITIDOS (nunca de proibidos). Tudo que nao esta aqui
 * (custo, margem, fornecedor, dado de cliente, descricao livre, especificacoes,
 * qualquer campo novo) e descartado antes de chegar ao aparelho.
 */
const ENVELOPE_FIELDS = new Set(['page', 'hasMore']);
const PRODUCT_FIELDS = new Set(['id', 'name', 'priceCents', 'unit', 'code', 'sku', 'barcode', 'image', 'archived', 'available']);
const VARIANT_FIELDS = new Set(['id', 'name', 'priceCents', 'sku', 'barcode', 'code', 'attributes', 'archived', 'available']);
const isObject = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === 'object' && !Array.isArray(v);

function pick(source: Record<string, unknown>, allowed: Set<string>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(source)) if (allowed.has(key) && source[key] !== undefined) out[key] = source[key];
  return out;
}

function pickAttributes(value: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (isObject(value))
    for (const [k, v] of Object.entries(value)) if (typeof v === 'string' || typeof v === 'number') out[k] = String(v);
  return out;
}

function pickMedia(value: unknown): Array<{ data: string; alt: string }> {
  if (!Array.isArray(value)) return [];
  return value.flatMap((m) =>
    isObject(m) && typeof m.data === 'string' && /^data:image\/(png|jpeg);base64,/.test(m.data)
      ? [{ data: m.data, alt: typeof m.alt === 'string' ? m.alt : '' }]
      : [],
  );
}

function sanitizeVariant(raw: unknown): Record<string, unknown> | null {
  if (!isObject(raw)) return null;
  const out = pick(raw, VARIANT_FIELDS);
  if ('attributes' in out) out.attributes = pickAttributes(raw.attributes);
  if (raw.media !== undefined) out.media = pickMedia(raw.media);
  return out;
}

function sanitizeProduct(raw: unknown): Record<string, unknown> | null {
  if (!isObject(raw)) return null;
  const out = pick(raw, PRODUCT_FIELDS);
  if (raw.media !== undefined) out.media = pickMedia(raw.media);
  if (Array.isArray(raw.variants))
    out.variants = raw.variants.map(sanitizeVariant).filter((v): v is Record<string, unknown> => v !== null);
  return out;
}

/** Reduz a resposta do catalogo aos campos permitidos; formato desconhecido vira null (nao grava). */
export function sanitizeCatalogPayload(payload: unknown): unknown {
  if (!isObject(payload)) return null;
  const out: Record<string, unknown> = pick(payload, ENVELOPE_FIELDS);
  if (Array.isArray(payload.items)) out.items = payload.items.map(sanitizeProduct).filter((p) => p !== null);
  else if ('items' in payload) return null;
  if (payload.product !== undefined) out.product = sanitizeProduct(payload.product);
  if (payload.variant !== undefined) out.variant = sanitizeVariant(payload.variant);
  if (!('items' in out) && !('product' in out) && !('variant' in out)) {
    const single = sanitizeProduct(payload);
    return single && 'id' in single ? single : null;
  }
  return out;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** "Dados de 14:05, sem internet." (com a data quando nao e de hoje). */
export function offlineAgeNotice(savedAt: number, now: number = Date.now()): string {
  const d = new Date(savedAt);
  const hhmm = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  const today = new Date(now);
  const sameDay = d.toDateString() === today.toDateString();
  return sameDay
    ? `Dados de ${hhmm}, sem internet.`
    : `Dados de ${pad(d.getDate())}/${pad(d.getMonth() + 1)} ${hhmm}, sem internet.`;
}
const TOOL_PATH = /^\/modules\/tools\/(catalogo\.[a-z0-9-]+)\/([a-z-]+)$/;

export type CatalogRequestKind = 'cacheable-read' | 'catalog-write' | 'other';

export function classifyCatalogRequest(method: string, path: string): CatalogRequestKind {
  const m = TOOL_PATH.exec(path.split('?')[0] ?? '');
  if (!m) return 'other';
  const tool = m[1]!;
  const action = m[2]!;
  const verb = method.toUpperCase();
  if (verb === 'POST' && action === 'read') return READ_TOOLS.has(tool) ? 'cacheable-read' : 'other';
  if (verb !== 'GET' && action !== 'read') return 'catalog-write';
  return 'other';
}

function stable(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';
  if (Array.isArray(value)) return `[${value.map(stable).join(',')}]`;
  const o = value as Record<string, unknown>;
  return `{${Object.keys(o)
    .sort()
    .filter((k) => o[k] !== undefined)
    .map((k) => `${JSON.stringify(k)}:${stable(o[k])}`)
    .join(',')}}`;
}

/** Resumo FNV-1a do conteudo: muda quando preco ou item muda. */
export function catalogVersion(payload: unknown): string {
  const text = stable(payload);
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16);
}

const entryKey = (s: CatalogCacheScope, tool: string, body: unknown) =>
  `${PREFIX}${s.companyId}:${s.userId}:${tool}:${catalogVersion(body ?? {})}`;

interface Entry {
  version: string;
  savedAt: number;
  payload: unknown;
}

/** Grava a copia apos uma leitura bem-sucedida. Devolve true se algo mudou. */
export function recordCatalogRead(
  store: CacheStore,
  scope: CatalogCacheScope | null,
  tool: string,
  body: unknown,
  payload: unknown,
  now: () => number = Date.now,
): boolean {
  if (!scope || !READ_TOOLS.has(tool)) return false;
  try {
    const safe = sanitizeCatalogPayload(payload);
    if (safe === null) return false;
    payload = safe;
    const key = entryKey(scope, tool, body);
    const version = catalogVersion(payload);
    const previous = store.getItem(key);
    if (previous && (JSON.parse(previous) as Entry).version === version) return false;
    const serialized = JSON.stringify({ version, savedAt: now(), payload } satisfies Entry);
    if (serialized.length > MAX_ENTRY_CHARS) {
      store.removeItem(key);
      return false;
    }
    store.setItem(key, serialized);
    return true;
  } catch {
    return false;
  }
}

/** Copia para uso SOMENTE quando a rede falhou. */
export function readCatalogFallback<T>(
  store: CacheStore,
  scope: CatalogCacheScope | null,
  tool: string,
  body: unknown,
): { payload: T; savedAt: number; version: string } | null {
  if (!scope || !READ_TOOLS.has(tool)) return null;
  try {
    const raw = store.getItem(entryKey(scope, tool, body));
    if (!raw) return null;
    const e = JSON.parse(raw) as Entry;
    return { payload: e.payload as T, savedAt: e.savedAt, version: e.version };
  } catch {
    return null;
  }
}

function removeWhere(store: CacheStore, match: (key: string) => boolean): number {
  const keys: string[] = [];
  for (let i = 0; i < store.length; i += 1) {
    const k = store.key(i);
    if (k && k.startsWith(PREFIX) && match(k)) keys.push(k);
  }
  keys.forEach((k) => store.removeItem(k));
  return keys.length;
}

/** Preco ou item mudou na empresa: descarta as copias de todos os usuarios dela. */
export function invalidateCatalogCache(store: CacheStore, companyId: string): number {
  try {
    return removeWhere(store, (k) => k.startsWith(`${PREFIX}${companyId}:`));
  } catch {
    return 0;
  }
}

/** Sair da conta: apaga tudo, de qualquer empresa. */
export function clearCatalogCache(store: CacheStore): number {
  try {
    return removeWhere(store, () => true);
  } catch {
    return 0;
  }
}

// ---- Ligacao com o navegador (fina; a logica acima e a testada) ----

let activeScope: CatalogCacheScope | null = null;

function browserStore(): CacheStore | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/**
 * Regra do escopo (testavel): sem sessao valida apaga tudo; com sessao apaga toda copia
 * que nao seja desta empresa E deste usuario (troca de empresa, de usuario ou carga nova
 * com sobras do aparelho).
 */
export function applyCatalogScope(store: CacheStore, scope: CatalogCacheScope | null): number {
  if (!scope) return clearCatalogCache(store);
  const own = `${PREFIX}${scope.companyId}:${scope.userId}:`;
  try {
    return removeWhere(store, (k) => !k.startsWith(own));
  } catch {
    return 0;
  }
}

/** Define quem esta autenticado; null (saida ou sessao expirada) limpa tudo. */
export function setCatalogCacheScope(scope: CatalogCacheScope | null): void {
  activeScope = scope;
  offlineSavedAt = null;
  notifyOffline();
  const store = browserStore();
  if (!store) return;
  applyCatalogScope(store, scope);
  if (!scope) clearServiceWorkerCaches();
}

let offlineSavedAt: number | null = null;
const offlineListeners = new Set<() => void>();
function notifyOffline(): void {
  offlineListeners.forEach((l) => l());
}
/** Idade da ultima copia servida sem internet (null = dados ao vivo). */
export function getOfflineSavedAt(): number | null {
  return offlineSavedAt;
}
export function subscribeOfflineCopy(listener: () => void): () => void {
  offlineListeners.add(listener);
  return () => void offlineListeners.delete(listener);
}

export function catalogCacheOnSuccess(method: string, path: string, body: unknown, payload: unknown): void {
  const store = browserStore();
  if (!store || !activeScope) return;
  const kind = classifyCatalogRequest(method, path);
  if (kind === 'cacheable-read') {
    if (offlineSavedAt !== null) {
      offlineSavedAt = null;
      notifyOffline();
    }
    const tool = /\/modules\/tools\/([^/]+)\/read/.exec(path)![1]!;
    recordCatalogRead(store, activeScope, tool, body, payload);
  } else if (kind === 'catalog-write') {
    invalidateCatalogCache(store, activeScope.companyId);
  }
}

export function catalogCacheFallback<T>(method: string, path: string, body: unknown): T | null {
  const store = browserStore();
  if (!store || classifyCatalogRequest(method, path) !== 'cacheable-read') return null;
  const tool = /\/modules\/tools\/([^/]+)\/read/.exec(path)![1]!;
  const hit = readCatalogFallback<T>(store, activeScope, tool, body);
  if (!hit) return null;
  offlineSavedAt = hit.savedAt;
  notifyOffline();
  return hit.payload;
}

export function clearServiceWorkerCaches(): void {
  try {
    navigator.serviceWorker?.controller?.postMessage({ type: 'rimati:clear-caches' });
  } catch {
    // Sem service worker nao ha o que limpar.
  }
}

export function registerServiceWorker(): void {
  if (!import.meta.env.PROD || typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => undefined);
  });
}
